import { FC, ReactNode, useMemo } from "react";
import { Paper, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { formatCompactNumber } from "../../../../../../../common/utils";
import { ChartNoDataOverlay } from "../../../../../../../components/charts/components/ChartNoDataOverlay";
import { GraphBarChartLegend } from "../../../../../../../components/charts/components/GraphBarChartLegend";

export type DoubleHorizontalBarItem = {
	id: string | number;
	label: string;
	increase: number;
	decrease: number;
	icon?: ReactNode;
	increaseTooltip?: ReactNode;
	decreaseTooltip?: ReactNode;
};

type Props = {
	data: DoubleHorizontalBarItem[];
	title: string;
	increaseLabel?: string;
	decreaseLabel?: string;
	measure?: string;
	height?: number;
	intervalsPerSide?: number;
	axisPaddingRatio?: number;
	formatTick?: (value: number) => string;
	isLoading?: boolean;
};

type AxisConfiguration = {
	min: number;
	max: number;
	range: number;
	ticks: number[];
	zeroPosition: number;
};

const labelWidth = 72;
const rowSpacing = 8;
const chartPaddingX = 2;
const chartPaddingPx = chartPaddingX * 8;

const barHeight = 12;
const barGap = 6;

const regularTickHeight = 6;
const zeroTickHeight = 8;
const boundaryTickHeight = 10;

const regularLabelTop = 9;
const boundaryLabelTop = 27;

const comparisonTolerance = 0.000001;

/**
 * Returns a finite numeric value.
 */
const normalizeValue = (value: number | null | undefined): number => {
	return Number.isFinite(value) ? Number(value) : 0;
};

/**
 * Removes floating-point precision errors.
 */
const normalizePrecision = (value: number): number => {
	return Number(value.toPrecision(12));
};

/**
 * Checks if two numeric values are equivalent.
 */
const areEqual = (first: number, second: number): boolean => {
	return Math.abs(first - second) < comparisonTolerance;
};

/**
 * Removes duplicate numeric values.
 */
const getUniqueValues = (values: number[]): number[] => {
	return values.filter((value, index, items) => items.findIndex((item) => areEqual(item, value)) === index);
};

/**
 * Converts a value into its percentage position on the axis.
 */
const getAxisPosition = (value: number, min: number, range: number): number => {
	if (range <= 0) {
		return 50;
	}

	const position = ((value - min) / range) * 100;

	return Math.min(Math.max(position, 0), 100);
};

/**
 * Builds an axis using one shared scale on both sides.
 */
const buildAxisConfiguration = (
	data: DoubleHorizontalBarItem[],
	intervalsPerSide: number,
	axisPaddingRatio: number
): AxisConfiguration => {
	const maxIncrease = Math.max(...data.map((item) => Math.abs(normalizeValue(item.increase))), 0);

	const maxDecrease = Math.max(...data.map((item) => Math.abs(normalizeValue(item.decrease))), 0);

	// Keep a stable axis when all values are zero.
	if (maxIncrease === 0 && maxDecrease === 0) {
		return {
			min: -1,
			max: 1,
			range: 2,
			ticks: [-0.75, -0.5, -0.25, 0, 0.25, 0.5, 0.75],
			zeroPosition: 50,
		};
	}

	const safePaddingRatio = Math.max(axisPaddingRatio, 0);

	let min: number;
	let max: number;

	// All values are positive.
	if (maxDecrease === 0) {
		min = 0;

		max = normalizePrecision(maxIncrease * (1 + safePaddingRatio));
	}
	// All values are negative.
	else if (maxIncrease === 0) {
		min = normalizePrecision(-(maxDecrease * (1 + safePaddingRatio)));

		max = 0;
	}
	// Values exist on both sides.
	else {
		const dataSpan = maxDecrease + maxIncrease;

		const boundaryPadding = (dataSpan * safePaddingRatio) / 2;

		min = normalizePrecision(-(maxDecrease + boundaryPadding));

		max = normalizePrecision(maxIncrease + boundaryPadding);
	}

	const range = max - min;

	// Use the longest side to define one shared scale.
	const longestExtent = Math.max(maxDecrease, maxIncrease);

	const safeIntervals = Math.max(Math.floor(intervalsPerSide), 1);

	const tickStep = longestExtent / safeIntervals;

	const negativeTicks: number[] = [];
	const positiveTicks: number[] = [];

	for (let index = 1; index <= safeIntervals; index += 1) {
		const negativeTick = normalizePrecision(-(tickStep * index));

		const positiveTick = normalizePrecision(tickStep * index);

		// Keep regular negative ticks inside the axis.
		if (negativeTick > min && !areEqual(negativeTick, -maxDecrease)) {
			negativeTicks.push(negativeTick);
		}

		// Keep regular positive ticks inside the axis.
		if (positiveTick < max && !areEqual(positiveTick, maxIncrease)) {
			positiveTicks.push(positiveTick);
		}
	}

	const ticks = getUniqueValues([...negativeTicks.reverse(), 0, ...positiveTicks]);

	return {
		min,
		max,
		range,
		ticks,
		zeroPosition: getAxisPosition(0, min, range),
	};
};

const DoubleHorizontalBarChart: FC<Props> = ({
	data,
	title,
	increaseLabel = "Increase",
	decreaseLabel = "Decrease",
	measure = "tCO₂e",
	height = 220,
	intervalsPerSide = 4,
	axisPaddingRatio = 0.025,
	formatTick = (value) => formatCompactNumber(value, 0, 1),
	isLoading = false,
}) => {
	const normalizedData = useMemo(
		() =>
			data.map((item) => ({
				...item,
				increase: normalizeValue(item.increase),
				decrease: normalizeValue(item.decrease),
			})),
		[data]
	);

	const hasNoData = useMemo(
		() => normalizedData.length === 0 || normalizedData.every((item) => item.increase === 0 && item.decrease === 0),
		[normalizedData]
	);

	const axis = useMemo(
		() => buildAxisConfiguration(normalizedData, intervalsPerSide, axisPaddingRatio),
		[normalizedData, intervalsPerSide, axisPaddingRatio]
	);

	const getPosition = (value: number): number => {
		return getAxisPosition(value, axis.min, axis.range);
	};

	if (isLoading) {
		return <Skeleton variant="rounded" height={300} width="100%" />;
	}

	return (
		<Paper elevation={0} sx={{ p: 2 }}>
			<Typography variant="h6" pb={2}>
				{title}
			</Typography>

			<Stack spacing={2} width="100%" height={height}>
				<Stack spacing={1.5} flex={1} justifyContent="center" position="relative">
					{/* Vertical grid lines */}
					<Stack
						position="absolute"
						top={0}
						bottom={0}
						left={`calc(${labelWidth}px + ${rowSpacing}px + ${chartPaddingPx}px)`}
						right={`${chartPaddingPx}px`}
					>
						{axis.ticks.map((tick, index) => {
							const left = getPosition(tick);

							const isZero = areEqual(tick, 0);

							return (
								<Stack
									key={`grid-${tick}-${index}`}
									position="absolute"
									left={`${left}%`}
									top={0}
									bottom={0}
									width="1px"
									bgcolor="divider"
									sx={{
										transform: "translateX(-50%)",
										opacity: isZero ? 1 : 0.5,
									}}
								/>
							);
						})}

						{/* Left chart boundary */}
						<Stack
							position="absolute"
							left={0}
							top={0}
							bottom={0}
							width="1px"
							bgcolor="divider"
							sx={{
								opacity: 0.8,
							}}
						/>

						{/* Right chart boundary */}
						<Stack
							position="absolute"
							right={0}
							top={0}
							bottom={0}
							width="1px"
							bgcolor="divider"
							sx={{
								opacity: 0.8,
							}}
						/>
					</Stack>

					{hasNoData && <ChartNoDataOverlay />}

					{!hasNoData &&
						normalizedData.map((item) => {
							const increaseValue = Math.abs(item.increase);

							const decreaseValue = Math.abs(item.decrease);

							// Increase grows from zero to the right.
							const increaseEndPosition = getPosition(increaseValue);

							const increaseWidth = Math.max(increaseEndPosition - axis.zeroPosition, 0);

							// Decrease grows from zero to the left.
							const decreaseStartPosition = getPosition(-decreaseValue);

							const decreaseWidth = Math.max(axis.zeroPosition - decreaseStartPosition, 0);

							return (
								<Stack
									key={item.id}
									direction="row"
									alignItems="center"
									spacing={1}
									width="100%"
									position="relative"
									py={0.4}
								>
									{/* Y axis label */}
									<Stack
										width={labelWidth}
										minWidth={labelWidth}
										direction="row"
										alignItems="center"
										spacing={1}
									>
										{item.icon}

										<Typography variant="caption" color="text.secondary">
											{item.label}
										</Typography>
									</Stack>

									{/* Chart row */}
									<Stack flex={1} px={chartPaddingX}>
										<Stack position="relative" height={barHeight * 2 + barGap}>
											{/* Decrease bar */}
											{decreaseWidth > 0 && (
												<Tooltip
													title={item.decreaseTooltip ?? formatTick(item.decrease)}
													arrow
												>
													<Stack
														position="absolute"
														left={`${decreaseStartPosition}%`}
														top={0}
														width={`${decreaseWidth}%`}
														height={barHeight}
														borderRadius={1}
														bgcolor="success.main"
													/>
												</Tooltip>
											)}

											{/* Increase bar */}
											{increaseWidth > 0 && (
												<Tooltip
													title={item.increaseTooltip ?? formatTick(item.increase)}
													arrow
												>
													<Stack
														position="absolute"
														left={`${axis.zeroPosition}%`}
														top={barHeight + barGap}
														width={`${increaseWidth}%`}
														height={barHeight}
														borderRadius={1}
														bgcolor="error.main"
													/>
												</Tooltip>
											)}
										</Stack>
									</Stack>
								</Stack>
							);
						})}
				</Stack>

				{/* X axis */}
				<Stack direction="row" spacing={1} alignItems="center">
					<Stack width={labelWidth} minWidth={labelWidth} />

					<Stack flex={1} px={chartPaddingX}>
						<Stack position="relative" height={52}>
							{/* Horizontal axis line */}
							<Stack position="absolute" top={0} left={0} right={0} height="1px" bgcolor="divider" />

							{/* Regular ticks */}
							{axis.ticks.map((tick, index) => {
								const left = getPosition(tick);

								const isZero = areEqual(tick, 0);

								const isMin = areEqual(tick, axis.min);

								const isMax = areEqual(tick, axis.max);

								// Boundaries are rendered separately.
								if (isMin || isMax) {
									return null;
								}

								return (
									<Stack
										key={`tick-${tick}-${index}`}
										position="absolute"
										left={`${left}%`}
										top={0}
										alignItems="center"
										sx={{
											transform: "translateX(-50%)",
										}}
									>
										<Stack
											width="1px"
											height={isZero ? zeroTickHeight : regularTickHeight}
											bgcolor={isZero ? "text.primary" : "text.secondary"}
										/>

										<Typography
											variant="caption"
											color="text.secondary"
											whiteSpace="nowrap"
											position="absolute"
											top={regularLabelTop}
										>
											{formatTick(tick)}
										</Typography>
									</Stack>
								);
							})}

							{/* Left boundary */}
							<Stack position="absolute" left={0} top={0} alignItems="flex-start">
								<Stack width="1px" height={boundaryTickHeight} bgcolor="text.primary" />

								<Typography
									variant="caption"
									color="text.primary"
									fontWeight={600}
									whiteSpace="nowrap"
									position="absolute"
									top={boundaryLabelTop}
									left={0}
								>
									{formatTick(axis.min)}
								</Typography>
							</Stack>

							{/* Right boundary */}
							<Stack position="absolute" right={0} top={0} alignItems="flex-end">
								<Stack width="1px" height={boundaryTickHeight} bgcolor="text.primary" />

								<Typography
									variant="caption"
									color="text.primary"
									fontWeight={600}
									whiteSpace="nowrap"
									position="absolute"
									top={boundaryLabelTop}
									right={0}
								>
									{formatTick(axis.max)}
								</Typography>
							</Stack>
						</Stack>
					</Stack>
				</Stack>

				{/* Chart legend */}
				{!hasNoData && (
					<GraphBarChartLegend title={measure} primaryLable={increaseLabel} secondaryLable={decreaseLabel} />
				)}
			</Stack>
		</Paper>
	);
};

export default DoubleHorizontalBarChart;
